import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';

const ROWS_PER_PAGE = 25;

const SupplierList = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  const fetchSuppliers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: debouncedSearch.trim()
      });

      const res = await fetch(`${API_BASE_URL}/api/masters/supplier-get?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setSuppliers(data.data || []);
      setTotal(data.total || 0);
    } catch (error) {
      setError('Failed to load data');
      console.error('Fetch supplier error:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Overseas Supplier Records</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            onClick={() => navigate('/supplier/new')}
            className="flex items-center gap-1 bg-[#10a5e9] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : suppliers.length === 0 ? (
        <NoRecordFound message="No supplier records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Name</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Country</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Contact Email</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Bank</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Fax No</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Website</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Parent Company</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Currency</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Account No</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Swift Code</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">AD Code</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">IEC Code</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">GST No</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">FSSAI No</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">State</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">State Code</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s, i) => (
                <tr key={s.supplier_id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        
                  <td className="px-3 py-1 font-semibold whitespace-nowrap">{s.name}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.country}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.contact_email}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.bank}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.fax_no}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.website}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.parent_company}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.currency}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.account_no}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.swift_code}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.ad_code}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.iec_code}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.gst_no}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.fssai_no}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.state}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{s.state_code}</td>
                  <td className="px-3 py-1 whitespace-nowrap">
                    <button
                      onClick={() => navigate(`/supplier/${s.supplier_id}/edit`)}
                      className="text-xs px-2 py-1 bg-[#10a5e9] text-white rounded hover:bg-[#0e8ac2] border border-[#10a5e9] flex items-center gap-1"
                      title="Edit Supplier"
                    >
                      <FaEdit />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrev}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNext}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SupplierList;